import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { decideScrollTarget } from './scrollTarget';

/**
 * SPA scroll behavior (replaces the hash-only HashScroll):
 *
 *  - `#hash` anchor: scroll to the matching element (kept from HashScroll, with
 *    retries because target routes are lazily loaded and may not exist on paint).
 *  - Forward navigation (PUSH / REPLACE): scroll to top so a new page never
 *    opens at the previous page's scroll offset (the bug: clicking a subject
 *    card at the bottom of /curriculum landed /curriculum/:subject at its footer).
 *  - Back / forward navigation (POP): restore the scroll position that was
 *    recorded for that history entry, so the user returns to where they were
 *    (e.g. the physics card on /curriculum).
 *
 * Scroll positions are recorded continuously per history entry key on the
 * window `scroll` event.
 */
export default function ScrollManager() {
  const location = useLocation();
  const navType = useNavigationType();
  const positions = useRef(new Map());
  const currentKey = useRef(location.key);
  const prevPathname = useRef(location.pathname);

  // Continuously record the scroll position for the active history entry.
  useEffect(() => {
    const onScroll = () => {
      positions.current.set(currentKey.current, window.scrollY);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useLayoutEffect(() => {
    const { key, hash, pathname } = location;
    currentKey.current = key;

    // A navigation that stays on the same pathname — only the search / state /
    // hash query changed (e.g. switching Academics tabs, selecting a language
    // course unit, in-page filters) — must NOT move the scroll position. Only a
    // real page change (pathname differs) scrolls to top below; hash anchors are
    // handled further down. This is deliberately independent of the navigation
    // type so an unexpected PUSH / REPLACE / POP can never defeat it.
    const samePage = pathname === prevPathname.current;
    prevPathname.current = pathname;
    if (samePage && !hash) {
      return undefined;
    }

    const saved = positions.current.get(key);
    const target = decideScrollTarget({ navType, saved, hasHash: !!hash });

    // Hash anchor: scroll to the matching element (kept from HashScroll, with
    // retries because target routes are lazily loaded and may not exist on paint).
    if (target === 'hash') {
      const id = decodeURIComponent(hash.slice(1));
      if (!id) return undefined;
      let cancelled = false;
      const timers = [];
      const tryScroll = () => {
        if (cancelled) return true;
        const el = document.getElementById(id);
        if (!el) return false;
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return true;
      };
      if (!tryScroll()) {
        [0, 150, 400, 900].forEach((delay) => {
          timers.push(
            setTimeout(() => {
              if (!cancelled) tryScroll();
            }, delay),
          );
        });
      }
      return () => {
        cancelled = true;
        timers.forEach(clearTimeout);
      };
    }

    if (target === 0) {
      window.scrollTo(0, 0);
      return undefined;
    }

    // Restoring: the target page may still be laying out (lazy chunk / async
    // content), so retry until its height can accommodate the offset.
    let cancelled = false;
    let attempt = 0;
    const maxAttempts = 12;
    const timers = [];
    const tryRestore = () => {
      if (cancelled) return;
      attempt += 1;
      const maxY = document.documentElement.scrollHeight - window.innerHeight;
      if (maxY < target && attempt < maxAttempts) {
        timers.push(setTimeout(tryRestore, 60 * attempt));
        return;
      }
      window.scrollTo(0, Math.min(target, Math.max(maxY, 0)));
    };
    tryRestore();

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [location, navType]);

  return null;
}
